import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouterPath } from '../../routes/RouterPath';
import { MIN_PHONE_NUMBER_LENGTH, MIN_PASSWORD_LENGTH, MAX_PHONE_NUMBER_LENGTH, MAX_PASSWORD_LENGTH } from '@/consts';
import { getSessionUser, login } from '@/core/presenter/controller/LoginController';
import { encryptPassword } from '@/core/domain/usecases/passwordUsecase';
import { useStores } from '@/stores';

export default function useLogin() {
  const { presentationStore, userStore } = useStores();
  const [isSaveIdChecked, setIsSaveIdChecked] = useState(() => {
    return localStorage.getItem('savedPhoneNumber') !== null;
  });
  const [phoneNumber, setPhoneNumber] = useState(() => {
    return isSaveIdChecked ? localStorage.getItem('savedPhoneNumber') || '' : '';
  });
  const [password, setPassword] = useState('');
  const [isLoginErrorModalOpen, setIsLoginErrorModalOpen] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [loginErrorCount, setLoginErrorCount] = useState<string | undefined>(undefined);
  /** 로그인 버튼 비활성화 여부 */
  const isLoginBtnDisabled = phoneNumber.length < MIN_PHONE_NUMBER_LENGTH || password.length < MIN_PASSWORD_LENGTH;
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const sessionUser = await getSessionUser();

      if (sessionUser) {
        navigate(RouterPath.HOME);
      } else {
        presentationStore.clearStoreData();
        userStore.clearUserData();
      }
    };

    checkSession();
  }, [presentationStore, userStore, navigate]);

  /** input - phoneNumber 변경 이벤트 */
  const onChangePhoneNumber = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // 숫자만 추출
    const numbersOnly = value.replace(/[^0-9]/g, '');
    // 11자리로 제한
    const truncated = numbersOnly.slice(0, MAX_PHONE_NUMBER_LENGTH);
    setPhoneNumber(truncated);
  }, []);

  /** input - password 변경 이벤트 */
  const onChangePassword = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // 15자리로 제한
    const truncated = value.slice(0, MAX_PASSWORD_LENGTH);
    setPassword(truncated);
  }, []);

  /** 로그인 폼 제출 이벤트 */
  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (isSaveIdChecked) {
        localStorage.setItem('savedPhoneNumber', phoneNumber);
      }

      presentationStore.setPhoneNumber(phoneNumber);
      const encryptedPassword = await encryptPassword(presentationStore.phoneNumber, password);
      const result = await login({ phone: phoneNumber, password: encryptedPassword });

      if (result.succed) {
        navigate(RouterPath.HOME);
      } else if (result.errorProcceded && result.errorMessage) {
        setLoginErrorMessage(result.errorMessage);
        setLoginErrorCount(result.errorCount);
        setIsLoginErrorModalOpen(true);
      }
    },
    [isSaveIdChecked, presentationStore, phoneNumber, password, navigate],
  );

  /** 로그인 에러 모달 닫기 */
  const onClickLoginErrorModalClose = useCallback(() => {
    setIsLoginErrorModalOpen(false);
    setLoginErrorMessage('');
    setLoginErrorCount(undefined);
  }, []);

  /** 회원가입 버튼 클릭 이벤트 */
  const onClickJoinBtn = useCallback(() => navigate(RouterPath.JOIN), [navigate]);

  const handleSaveIdChange = useCallback(
    (isChecked: boolean) => {
      setIsSaveIdChecked(isChecked);
      if (!isChecked) {
        localStorage.removeItem('savedPhoneNumber');
      } else {
        // 체크박스 선택 시 현재 입력된 아이디 저장
        localStorage.setItem('savedPhoneNumber', phoneNumber);
      }
    },
    [phoneNumber],
  );

  return {
    phoneNumber,
    password,
    isLoginBtnDisabled,
    isSaveIdChecked,
    isLoginErrorModalOpen,
    loginErrorMessage,
    loginErrorCount,
    onChangePhoneNumber,
    onChangePassword,
    onSubmit,
    onClickJoinBtn,
    handleSaveIdChange,
    onClickLoginErrorModalClose,
  };
}
